import { useTranslation } from "react-i18next";
import { Card } from "reactstrap";
import Drawer from "../../../../../components/Drawer";
import Form from "./Form";
import BulkActions from "../../../components/BulkActions";
import Topbar from "../../../components/Topbar";
import Actions from "../../../../../components/NTable/Actions";
import NTable from "../../../../../components/NTable";
import { useNewTable } from "../../../../../components/NTable/hook";
import { useColumns } from "./columns";
import useSettingsCRUDHook from "../../../hook";
import { useDrawerProps } from "../../../helpers/useDrawerProps";
import { useDeleteModal } from "../../../helpers/useDeleteModal";
import { useManageForm } from "../../../helpers/useManageForm";
import { useFetchResource } from "../../../helpers/useFetchResource";
import { useAccess, useActionWithAccess } from "../../../../../utils/hooks/access";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import HistoryDrawer from "./HistoryDrawer";


function MealPremium() {
	const { t } = useTranslation();

	const {
		state: { mode, data, totalItems, loading, submitting, error },
		selected,
		fetch,
		createSubmit,
		updateSubmit,
		goToHelp,
		goToView,
		goToCreate,
		goToEdit,
		goToClone,
		remove,
		batchRemove,
	} = useSettingsCRUDHook("/calc-rules/meal-premium");

	const { hasAccess: canAdd } = useAccess(
		"settings.payPolicies.calculationGroup.canCreate",
	);

	const {
        visible: isHistoryVisible,
        open: _openHistory,
        close: closeHistory,
        selected: selectedRowHistory
    } = useVisible();

	const openHistory = useActionWithAccess(_openHistory, "canViewAudits");

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: (data) =>
			Array.isArray(data)
				? t(
					"do-you-want-to-delete-meal-premiums", {
					count: data?.length
				},
				)
				: t(
					"do-you-want-to-delete-meal-premium"
				),
		onConfirm: (data) =>
			Array.isArray(data) ? batchRemove(data) : remove(data),
        permission: "settings.payPolicies.calculationGroup.canDelete",
	});

	const { unarchiveModal, isFormOpen, formProps } = useManageForm({
		mode,
		selected,
		createSubmit,
		updateSubmit,
	});

	const columns = useColumns({
		edit: useActionWithAccess(
			goToEdit,
			"settings.payPolicies.calculationGroup.canEdit",
		),
		remove: openDeleteModal,
		clone: useActionWithAccess(
			goToClone,
			"settings.payPolicies.calculationGroup.canCreate",
		),
		history: openHistory
	});

	const table = useNewTable({
        data,
        columns,
        loading,
        totalItems,
        sessionFiltersKey: "mealPremium",
    });

	const { open: drawerOpen, title: drawerTitle } = useDrawerProps({
		mode,
		help: t("help"),
		view: t("view-meal-premium"),
		create: t("add-meal-premium"),
		edit: t("edit-meal-premium"),
		clone: t("clone-meal-premium"),
	});

	useFetchResource({ fetch, state: table.getState() });

	return (
		<div className="d-flex h-100">
			<Card className="flex-fill mb-0">
				<Topbar
					title={t("meal-premium")}
					add={canAdd && goToCreate}
					help={goToHelp}
					loading={loading}
					history={!!openHistory && (() => openHistory())}
				/>

				<Card
					className="mb-auto"
					style={{ maxHeight: "calc(100% - 156px)" }}
				>
                    <Actions
						filters={table.getState().columnFilters}
						setFilters={table.setColumnFilters}
						columns={table.getAllColumns()}
                    >
                        <BulkActions
							rows={table.getState().rowSelection}
                            batchRemove={openDeleteModal}
                        />
                    </Actions>

                    <NTable table={table} />
				</Card>
			</Card>

			<Drawer open={drawerOpen} close={goToView} title={drawerTitle}>
				{isFormOpen ? (
					<Form
						{...formProps}
						error={error}
						loading={submitting}
						close={goToView}
					/>
				) : (
					<>{t("help")}</>
				)}
			</Drawer>

			{isHistoryVisible && (
                <HistoryDrawer open={isHistoryVisible} close={closeHistory} id={selectedRowHistory} />
            )}

			{deleteModal}

			{unarchiveModal}
		</div>
	);
}

export default MealPremium;
